import {
  APPRAISIFY_ADDRESS,
  APPRAISIFY_NAME,
  AloftPrice,
  orUndefined,
  schAloftDate,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { Tkv2PDF } from '../../tkv2PDF/types.ts';
import { AppraisalReportCoreData } from '../types.ts';

/**
 * Transform core appraisal report data into PDF-specific format
 */
export const buildPdfReportData = (coreData: AppraisalReportCoreData): Tkv2PDF => {
  const {
    order,
    subjectAddress,
    effectiveDate,
    inspectionDate,
    subjectCoordinates,
    subjectPropertyImage,
    compSequence,
    compTypes,
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    comparablesSalesComparisonData,
    salesComparablePhotosData,
    costApproachData,
    reconciliationPageData,
    priorSales,
    compsForMaps,
    mapBoxCoordinates,
    mapStatistics,
    adjustmentSettings,
    manualAdjustmentsEntities,
    commentaryV2,
    adjustmentsTableData,
    arvAdjustmentsTableData,
    hasFunctionalDepreciation,
    hasExternalDepreciation,
    dateOfSaleChartParams,
    siteValueChartParams,
    appraiserName,
    alerts,
    marketAggregateData,
    hasArvOption,
    appraisalReportDisclaimer,
    zoning,
    zoningCompliance,
    parcelNumber,
    propertyInterestValue,
    hoa,
    locationType,
    hoaFees,
    pudDisplay,
    legalDescription,
    satelliteViewStaticUrl,
    appraisalType,
    shortPropertyType,
    propertyType,
    opinionOfMarketValue,
    totalCostToCure,
    totalRenovationBudget,
    opinionOfAfterRepairValue,
    estimatedROI,
    hasNegativeROI,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    signedAt,
    revisionType,
    CountyOrParish,
    CountyOrParishFIPS,
    valueType,
    salesComparisonApproach,
    includeCostApproach,
    isInternalValuation,
  } = coreData;

  return {
    subjectAddress: subjectAddress ?? '',
    opinionOfMarketValue: opinionOfMarketValue
      ? schAloftPrice.parse(opinionOfMarketValue).toString(true)
      : '-',
    totalCostToCure: orUndefined(schAloftPrice.parse)(totalCostToCure)?.toString(true) ?? '-',
    totalRenovationBudget:
      orUndefined(schAloftPrice.parse)(totalRenovationBudget)?.toString(true) ?? '-',
    opinionOfAfterRepairValue:
      orUndefined(schAloftPrice.parse)(opinionOfAfterRepairValue)?.toString(true) ?? '-',
    estimatedROI: orUndefined(schAloftPrice.parse)(estimatedROI)?.toString(true) ?? '-',
    hasNegativeROI,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    clientName: order.appraisifyName ?? APPRAISIFY_NAME,
    clientAddress: order.appraisifyAddress ?? APPRAISIFY_ADDRESS,
    appraiserFee: order.appraiserFee ?? AloftPrice.zero.toJSON(),
    amcFee: order.appraisifyFee ?? AloftPrice.zero.toJSON(),
    borrowerName: order.borrowerName ?? '',
    mapBoxCoordinates,
    mapStatistics,
    intendedUsers: order.intendedUsers,
    intendedUse: order.intendedUse,
    propertyType,
    shortPropertyType,
    inspectorType: order.inspectorType ?? '',
    appraisalType,
    effectiveDate,
    inspectionDate,
    zoning,
    zoningCompliance,
    parcelNumber,
    propertyInterest: propertyInterestValue,
    hoa,
    locationType,
    hoaFees: hoaFees ?? 'None',
    pud: pudDisplay,
    legalDescription,
    propertyMapImage: satelliteViewStaticUrl,
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    comparablesSalesComparisonData,
    compsForMaps,
    subjectCoordinates: subjectCoordinates ?? { latitude: 0, longitude: 0 },
    adjustmentSettings,
    manualAdjustmentsEntities,
    commentaryV2,
    adjustmentsTableData,
    arvAdjustmentsTableData,
    hasFunctionalDepreciation,
    hasExternalDepreciation,
    chartParams: {
      dateOfSale: dateOfSaleChartParams,
      site: siteValueChartParams,
      aivReconciliationData: reconciliationPageData.aiv,
      arvReconciliationData: reconciliationPageData.arv,
    },
    licenseState: order.licenseState,
    licenseNumber: order.licenseNumber,
    licenseExpiration: orUndefined(schAloftDate.parse)(order.licenseExpiration)?.format(
      'MM/dd/yyyy',
    ),
    licenseType: order.licenseType,
    signedAt,
    appraiserName: appraiserName ?? '-',
    inspectionData: order.inspectionData,
    costApproachData,
    compSequence,
    priorSales,
    reconciliationPageData,
    isRevision: order.isRevision || false,
    revisionType,
    alerts: Object.values(alerts).flat(),
    marketAggregateData,
    countyName: CountyOrParish,
    countyFIPS: CountyOrParishFIPS,
    revisionData: order.revisionData,
    previousRevisionsData: order.previousRevisionsData,
    salesComparablePhotosData,
    hasArvOption,
    compTypes,
    salesComparisonApproach,
    valueType: valueType ?? '',
    appraisalReportDisclaimer,
    subjectPropertyImage,
    includeCostApproach,
    orderFeatures: order.orderFeatures,
    isInternalValuation,
    clientLogoUrl: order.clientLogoUrl,
  };
};
